import React, { useState, useEffect } from 'react';
import { BACKEND_URL, isDemoTenant, areDevAffordancesEnabled } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useInputKeyboardHandler } from '../hooks/useInputKeyboardHandler';
import LanguageToggle from './LanguageToggle';
import bocaLogo from '../boca_logo.svg';

export default function LoginScreen({ onLogin, theme, themeMode, onToggleTheme, logoSrc }) {
  const { t } = useLanguage();
  const { inputProps: keyboardInputProps, containerRef } = useInputKeyboardHandler();

  // Determine which credentials to show based on the tenant and environment
  const isDemoServer = isDemoTenant();
  const isDevServer = areDevAffordancesEnabled();
  const showDemoCredentials = isDemoServer || isDevServer;

  // Helper to check if we should skip PIN for testdev user
  const shouldSkipPin = (user) => {
    // Skip PIN for testdev on this machine or on the dev tenant
    const isTestDevUser = user?.user_name?.toLowerCase() === 'testdev';
    return areDevAffordancesEnabled() && isTestDevUser;
  };

  const [activeTab, setActiveTab] = useState('quick'); // 'quick' or 'full'
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginMsg, setLoginMsg] = useState("");
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin] = useState("");
  const [showSetPin, setShowSetPin] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  // Fetch active sessions on mount
  useEffect(() => {
    fetchActiveSessions();
  }, []);

  const fetchActiveSessions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/active-sessions`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      // Ensure data is an array
      const sessions = Array.isArray(data) ? data : [];
      setActiveSessions(sessions);
      // If there are active sessions, default to quick login tab
      if (sessions.length > 0) {
        setActiveTab('quick');
      } else {
        setActiveTab('full');
      }
    } catch (err) {
      console.error('Error fetching active sessions:', err);
      setActiveSessions([]);
      setActiveTab('full');
    }
  };

  // DEV ONLY: Autologin as testdev@gmail.com
  const handleAutoLogin = async () => {
    console.log('handleAutoLogin called');
    setLoginMsg("");
    try {
      console.log('Auto-login attempt to:', `${BACKEND_URL}/api/login`);
      const response = await fetch(`${BACKEND_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: "testdev@gmail.com", password: "testdev" })
      });
      const data = await response.json();
      if (data.success) {
        setLoginMsg("");
        // Check if user has PIN, if not prompt to set one
        if (!data.user.pin) {
          setShowSetPin(true);
          setSelectedUser(data.user);
        } else {
          onLogin(data.user);
        }
      } else {
        // Use error code to show translated message
        if (data.errorCode === 'USER_INACTIVE') {
          setLoginMsg(t('userInactive'));
        } else if (data.errorCode === 'INVALID_CREDENTIALS') {
          setLoginMsg(t('invalidCredentials'));
        } else {
          setLoginMsg(data.error || t('error'));
        }
      }
    } catch (err) {
      console.error('Auto-login error:', err);
      setLoginMsg(t('networkError') + ': ' + err.message);
    }
  };

  const handleLogin = async (e) => {
    console.log('handleLogin called', { loginEmail, loginPassword });
    e.preventDefault();
    setLoginMsg("");
    if (!loginEmail || !loginPassword) {
      console.log('Missing credentials');
      setLoginMsg(t('email') + ' and ' + t('password') + ' required');
      return;
    }
    try {
      console.log('Attempting login to:', `${BACKEND_URL}/api/login`);
      const response = await fetch(`${BACKEND_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      console.log('Login response status:', response.status);
      const data = await response.json();
      console.log('Login response data:', data);
      if (data.success) {
        console.log('Login successful, calling onLogin');
        setLoginMsg("");
        // Check if user has PIN, if not prompt to set one
        if (!data.user.pin) {
          setShowSetPin(true);
          setSelectedUser(data.user);
        } else {
          onLogin(data.user);
        }
      } else {
        // Use error code to show translated message
        if (data.errorCode === 'USER_INACTIVE') {
          setLoginMsg(t('userInactive'));
        } else if (data.errorCode === 'INVALID_CREDENTIALS') {
          setLoginMsg(t('invalidCredentials'));
        } else {
          setLoginMsg(data.error || t('error'));
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginMsg(t('networkError') + ': ' + err.message);
    }
  };

  const handlePinLogin = async () => {
    if (!selectedUser || !pin || pin.length !== 4) {
      setLoginMsg(t('enterPin'));
      return;
    }

    setLoginMsg("");
    try {
      console.log('Attempting PIN login to:', `${BACKEND_URL}/api/pin-login`);
      const response = await fetch(`${BACKEND_URL}/api/pin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: selectedUser.id, pin })
      });
      const data = await response.json();
      if (data.success) {
        setLoginMsg("");
        setPin("");
        setSelectedUser(null);
        onLogin(data.user);
      } else {
        setPin("");
        if (data.attemptsRemaining !== undefined) {
          setLoginMsg(`${t('invalidCredentials')}. ${data.attemptsRemaining} ${t('attemptsRemaining')}`);
        } else {
          setLoginMsg(data.error || t('invalidCredentials'));
        }
      }
    } catch (err) {
      setLoginMsg(t('networkError') + ': ' + err.message);
      setPin("");
    }
  };

  const handleSetPin = async () => {
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setLoginMsg(t('enterPin') + " (4 " + t('digits') + ")");
      return;
    }
    if (newPin !== confirmPin) {
      setLoginMsg(t('pinsDoNotMatch'));
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/user/${selectedUser.id}/pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: newPin })
      });
      const data = await response.json();
      if (data.success) {
        setShowSetPin(false);
        setNewPin("");
        setConfirmPin("");
        selectedUser.pin = newPin; // Update local user object
        onLogin(selectedUser);
      } else {
        setLoginMsg(data.error || t('error'));
      }
    } catch (err) {
      setLoginMsg(t('networkError') + ': ' + err.message);
    }
  };

  // Mask PIN display (convert to dots)
  const maskPin = (pin) => {
    return '•'.repeat(pin.length);
  };

  // Render Set PIN Modal
  const renderSetPinModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius || 8,
        padding: '2rem',
        maxWidth: '400px',
        width: '90%',
        boxShadow: theme.boxShadow
      }}>
        <h3 style={{ color: theme.colors.primary, marginTop: 0 }}>{t('setPin')}</h3>
        <p style={{ color: theme.colors.text, fontSize: '0.9rem', marginBottom: '1.5rem' }}>

        </p>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleSetPin();
        }} autoComplete="off">
          {/* Hidden username field to prevent password manager detection */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={selectedUser?.user_name || ''}
            readOnly
            tabIndex={-1}
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px'
            }}
            aria-hidden="true"
          />
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
              {t('enterPin')}:
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength="4"
              value={maskPin(newPin)}
              onChange={(e) => {
                const input = e.target.value;
                if (input.length > maskPin(newPin).length) {
                  const newDigit = input.slice(-1);
                  if (/^\d$/.test(newDigit)) {
                    setNewPin(newPin + newDigit);
                  }
                } else if (input.length < maskPin(newPin).length) {
                  setNewPin(newPin.slice(0, -1));
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  setNewPin(newPin.slice(0, -1));
                } else if (/^\d$/.test(e.key) && newPin.length < 4) {
                  e.preventDefault();
                  setNewPin(newPin + e.key);
                }
              }}
              onFocus={keyboardInputProps.onFocus}
              autoComplete="off"
              name="verification-code"
              autoFocus
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                borderRadius: theme.borderRadius || 4,
                border: `2px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
              placeholder="••••"
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
              {t('confirmPin')}:
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength="4"
              value={maskPin(confirmPin)}
              onChange={(e) => {
                const input = e.target.value;
                if (input.length > maskPin(confirmPin).length) {
                  const newDigit = input.slice(-1);
                  if (/^\d$/.test(newDigit)) {
                    setConfirmPin(confirmPin + newDigit);
                  }
                } else if (input.length < maskPin(confirmPin).length) {
                  setConfirmPin(confirmPin.slice(0, -1));
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  setConfirmPin(confirmPin.slice(0, -1));
                } else if (/^\d$/.test(e.key) && confirmPin.length < 4) {
                  e.preventDefault();
                  setConfirmPin(confirmPin + e.key);
                }
              }}
              onFocus={keyboardInputProps.onFocus}
              autoComplete="off"
              name="verification-code-confirm"
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                borderRadius: theme.borderRadius || 4,
                border: `2px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
              placeholder="••••"
            />
          </div>

          {loginMsg && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              backgroundColor: theme.colors.error + '20',
              color: theme.colors.error,
              borderRadius: theme.borderRadius || 4,
              textAlign: 'center'
            }}>
              {loginMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => {
                setShowSetPin(false);
                setNewPin("");
                setConfirmPin("");
                setLoginMsg("");
                onLogin(selectedUser); // Login without PIN
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: theme.colors.secondary,
                color: theme.colors.textLight,
                border: 'none',
                borderRadius: theme.borderRadius || 4,
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Skip
            </button>
            <button
              type="submit"
              disabled={newPin.length !== 4 || confirmPin.length !== 4}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: (newPin.length === 4 && confirmPin.length === 4) ? theme.colors.primary : '#ccc',
                color: theme.colors.textLight,
                border: 'none',
                borderRadius: theme.borderRadius || 4,
                fontWeight: 'bold',
                cursor: (newPin.length === 4 && confirmPin.length === 4) ? 'pointer' : 'not-allowed'
              }}
            >
              Set PIN
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 0,
      background: theme.colors.background,
      color: theme.colors.text,
      fontFamily: theme.fonts.main,
      transition: 'background 0.3s, color 0.3s',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
    }}>
      <div style={{
        position: 'relative',
        top: 0,
        left: 0,
        width: '100vw',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        paddingBottom: '50vh',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '90%', maxWidth: '600px', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <img
              src={logoSrc}
              alt="Boca POS"
              style={{ maxWidth: '50vw', minWidth: '120px', width: '100%', height: 'auto', marginRight: '2vw' }}
              onError={(e) => { e.target.onerror = null; e.target.src = bocaLogo; }}
            />
          </div>
          <LanguageToggle theme={theme} />
          {onToggleTheme && (
            <button
              onClick={onToggleTheme}
              style={{
                padding: '0.5rem 0.75rem',
                minWidth: '48px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: theme?.borderRadius || '6px',
                border: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                background: theme?.colors?.surface || '#fff',
                color: theme?.colors?.primary || '#1976d2',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1.1rem',
              }}
            >
              {themeMode === 'dark' ? '🌞' : '🌙'}
            </button>
          )}
        </div>

        <div style={{
          maxWidth: '600px',
          width: '90%',
          padding: '2rem',
          border: `1px solid ${theme.colors.border}`,
          background: theme.colors.surface,
          color: theme.colors.text,
          borderRadius: theme.borderRadius,
          boxShadow: theme.boxShadow,
          position: 'relative',
        }}>
          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '1.5rem',
            borderBottom: `2px solid ${theme.colors.border}`
          }}>
            <button
              onClick={() => setActiveTab('quick')}
              disabled={activeSessions.length === 0}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: 'transparent',
                color: activeTab === 'quick' ? theme.colors.primary : theme.colors.textSecondary,
                border: 'none',
                borderBottom: `3px solid ${activeTab === 'quick' ? theme.colors.primary : 'transparent'}`,
                fontWeight: activeTab === 'quick' ? 'bold' : 'normal',
                cursor: activeSessions.length > 0 ? 'pointer' : 'not-allowed',
                opacity: activeSessions.length > 0 ? 1 : 0.5,
                fontSize: '1rem'
              }}
            >
              ⚡ {t('quickLogin')} ({activeSessions.length})
            </button>
            <button
              onClick={() => setActiveTab('full')}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: 'transparent',
                color: activeTab === 'full' ? theme.colors.primary : theme.colors.textSecondary,
                border: 'none',
                borderBottom: `3px solid ${activeTab === 'full' ? theme.colors.primary : 'transparent'}`,
                fontWeight: activeTab === 'full' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              🔐 {t('Full Login')}
            </button>
          </div>

          {/* Quick Login Tab */}
          {activeTab === 'quick' && !selectedUser && (
            <div>
              <h3 style={{ textAlign: 'center', marginTop: 0, color: theme.colors.primary }}>
                Select Your Account
              </h3>
              {/* Demo credentials info */}
              {showDemoCredentials && (
                <div style={{
                  background: theme.colors.background|| '#3083beff',
                  color: theme.colors.text || '#333',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius,
                  padding: '0.5rem 1rem',
                  margin: '0.5rem auto 1rem auto',
                  maxWidth: 500,
                  fontSize: '0.85rem',
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  boxShadow: theme.boxShadow,
                }}>
                  <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                    <strong>{isDemoServer ? 'Demo' : 'Dev'} User {t('Credentials')}:</strong>
                  </div>
                  {isDemoServer ? (
                    <>
                      <div><strong>Manager:</strong> manager@gmail.com / manager / {t('pin')}: 1234</div>
                      <div><strong>Server:</strong> server@gmail.com / server / {t('pin')}: 1234</div>
                      <div><strong>Bar:</strong> bar@gmail.com / bar / {t('pin')}: 1234</div>
                      <div><strong>Owner:</strong> owner@gmail.com / owner / {t('pin')}: 1234</div>
                      <div><strong>Kitchen:</strong> kitchen@gmail.com / kitchen / {t('pin')}: 1234</div>
                    </>
                  ) : (
                    <>
                      <div><strong>Manager:</strong> manager@dev.boca.mx / manager / {t('pin')}: 1234</div>
                      <div><strong>Server:</strong> server@dev.boca.mx / server / {t('pin')}: 1234</div>
                      <div><strong>Bar:</strong> bar@dev.boca.mx / bar / {t('pin')}: 1234</div>
                      <div><strong>Owner:</strong> owner@dev.boca.mx / owner / {t('pin')}: 1234</div>
                      <div><strong>Kitchen:</strong> kitchen@dev.boca.mx / kitchen / {t('pin')}: 1234</div>
                    </>
                  )}
                </div>
              )}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '1rem',
                marginTop: '1.5rem'
              }}>
                {activeSessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={async () => {
                      // Skip PIN for testdev on local/dev environments
                      if (shouldSkipPin(session)) {
                        // Use PIN '1234' for testdev auto-login
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/pin-login`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ userId: session.id, pin: '1234' })
                          });
                          const data = await response.json();
                          if (data.success) {
                            onLogin(data.user);
                          } else {
                            setLoginMsg(data.error || 'Failed to login');
                          }
                        } catch (err) {
                          console.error('Error during auto-login:', err);
                          setLoginMsg(t('networkError') + ': ' + err.message);
                        }
                      } else {
                        setSelectedUser(session);
                      }
                    }}
                    style={{
                      padding: '1rem',
                      backgroundColor: theme.colors.primary + '20',
                      border: `2px solid ${theme.colors.primary}`,
                      borderRadius: theme.borderRadius || 8,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      fontWeight: 'bold',
                      color: theme.colors.text
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary + '40';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary + '20';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👤</div>
                    <div style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>
                      {session.user_name || session.first_names || 'User'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PIN Entry */}
          {activeTab === 'quick' && selectedUser && (
            <div>
              <h3 style={{ textAlign: 'center', marginTop: 0, color: theme.colors.primary }}>
                {t('welcomeBack')}, {selectedUser.user_name || selectedUser.first_names}!
              </h3>
              <p style={{ textAlign: 'center', color: theme.colors.textSecondary, fontSize: '0.9rem' }}>
                {t('enterYourPin')}
              </p>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (pin.length === 4) {
                  handlePinLogin();
                }
              }} autoComplete="off">
                {/* Hidden username field to prevent password manager detection */}
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={selectedUser?.user_name || ''}
                  readOnly
                  tabIndex={-1}
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px'
                  }}
                  aria-hidden="true"
                />
                <div style={{ marginBottom: '1.5rem', marginTop: '2rem' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength="4"
                    value={maskPin(pin)}
                    onChange={(e) => {
                      // Extract actual digits from input
                      const input = e.target.value;
                      // If user is typing (length increases), add the new digit
                      if (input.length > maskPin(pin).length) {
                        const newDigit = input.slice(-1);
                        if (/^\d$/.test(newDigit)) {
                          setPin(pin + newDigit);
                        }
                      }
                      // If user is deleting (length decreases)
                      else if (input.length < maskPin(pin).length) {
                        setPin(pin.slice(0, -1));
                      }
                    }}
                    onKeyDown={(e) => {
                      // Handle backspace
                      if (e.key === 'Backspace') {
                        e.preventDefault();
                        setPin(pin.slice(0, -1));
                      }
                      // Handle number keys
                      else if (/^\d$/.test(e.key) && pin.length < 4) {
                        e.preventDefault();
                        setPin(pin + e.key);
                      }
                    }}
                    onFocus={keyboardInputProps.onFocus}
                    autoComplete="off"
                    name="verification-code"
                    autoFocus
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      padding: '1rem',
                      fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                      textAlign: 'center',
                      letterSpacing: 'clamp(0.5rem, 2vw, 1rem)',
                      borderRadius: theme.borderRadius || 4,
                      border: `2px solid ${theme.colors.primary}`,
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text,
                      boxSizing: 'border-box',
                      margin: '0 auto',
                      display: 'block'
                    }}
                    placeholder="••••"
                  />
                </div>

                {loginMsg && (
                  <div style={{
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    backgroundColor: theme.colors.error + '20',
                    color: theme.colors.error,
                    borderRadius: theme.borderRadius || 4,
                    textAlign: 'center'
                  }}>
                    {loginMsg}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      setPin("");
                      setLoginMsg("");
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: theme.colors.secondary,
                      color: theme.colors.textLight,
                      border: 'none',
                      borderRadius: theme.borderRadius || 4,
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={pin.length !== 4}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: pin.length === 4 ? theme.colors.primary : '#ccc',
                      color: theme.colors.textLight,
                      border: 'none',
                      borderRadius: theme.borderRadius || 4,
                      fontWeight: 'bold',
                      cursor: pin.length === 4 ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Login →
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Full Login Tab */}
          {activeTab === 'full' && (
            <div>
              <h2 style={{ textAlign: 'center', marginTop: 0 }}>{t('login')}</h2>
              {/* Demo credentials info */}
              {showDemoCredentials && (
                <div style={{
                  background: theme.colors.background || '#e3f2fd',
                  color: theme.colors.text || '#333',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius,
                  padding: '0.5rem 1rem',
                  margin: '0.5rem auto 1rem auto',
                  maxWidth: 500,
                  fontSize: '0.85rem',
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  boxShadow: theme.boxShadow,
                }}>
                  <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                    <strong>{isDemoServer ? 'Demo' : 'Dev'} User {t('Credentials')}:</strong>
                  </div>
                  {isDemoServer ? (
                    <>
                      <div><strong>Manager:</strong> manager@gmail.com / manager / {t('pin')}: 1234</div>
                      <div><strong>Server:</strong> server@gmail.com / server / {t('pin')}: 1234</div>
                      <div><strong>Bar:</strong> bar@gmail.com / bar / {t('pin')}: 1234</div>
                      <div><strong>Owner:</strong> owner@gmail.com / owner / {t('pin')}: 1234</div>
                      <div><strong>Kitchen:</strong> kitchen@gmail.com / kitchen / {t('pin')}: 1234</div>
                    </>
                  ) : (
                    <>
                      <div><strong>Manager:</strong> manager@dev.boca.mx / manager / {t('pin')}: 1234</div>
                      <div><strong>Server:</strong> server@dev.boca.mx / server / {t('pin')}: 1234</div>
                      <div><strong>Bar:</strong> bar@dev.boca.mx / bar / {t('pin')}: 1234</div>
                      <div><strong>Owner:</strong> owner@dev.boca.mx / owner / {t('pin')}: 1234</div>
                      <div><strong>Kitchen:</strong> kitchen@dev.boca.mx / kitchen / {t('pin')}: 1234</div>
                    </>
                  )}
                </div>
              )}
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: '1rem' }}>
                  <label>{t('email')}:</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    onFocus={keyboardInputProps.onFocus}
                    required
                    style={{
                      marginLeft: '0.5rem',
                      background: theme.colors.background,
                      color: theme.colors.text,
                      borderRadius: theme.borderRadius,
                      border: `1px solid ${theme.colors.border}`
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label>{t('password')}:</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    onFocus={keyboardInputProps.onFocus}
                    required
                    style={{
                      marginLeft: '0.5rem',
                      background: theme.colors.background,
                      color: theme.colors.text,
                      borderRadius: theme.borderRadius,
                      border: `1px solid ${theme.colors.border}`
                    }}
                  />
                </div>
                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: theme.colors.primary,
                    color: theme.colors.textLight,
                    border: 'none',
                    borderRadius: theme.borderRadius,
                    fontWeight: 'bold'
                  }}
                >
                  {t('loginButton')}
                </button>
                {showDemoCredentials && (
                  <button
                    type="button"
                    onClick={handleAutoLogin}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: theme.colors.secondary,
                      color: theme.colors.textLight,
                      border: 'none',
                      borderRadius: theme.borderRadius,
                      fontWeight: 'bold',
                      marginTop: '0.5rem'
                    }}
                  >
                    {t('devLogin')}
                  </button>
                )}
                {loginMsg && (
                  <div style={{
                    marginTop: '1rem',
                    color: theme.colors.error,
                    textAlign: 'center'
                  }}>
                    {loginMsg}
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Set PIN Modal */}
      {showSetPin && renderSetPinModal()}
    </div>
  );
}
